/**
 * HiddenItemsTab — tab "Ocultos" del panel Data Health.
 *
 * Lista productos con visibility=0. Filtros por hidden_reason. Bulk action
 * principal: "Mostrar" (un-hide). El resto de bulk actions (editar marca,
 * subcat, etc.) están stubbed con tooltip "Próximamente" para v2.
 *
 * v1 MVP: solo bulk-unhide funcional + drawer detalle.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, EyeOff, X } from 'lucide-react';
import type { DataHealthItem, HiddenReasonShort } from '../../../types/dataHealth';
import {
  bulkUnhide,
  errorMessage,
  isBackendUnavailable,
  listHidden,
} from '../../../services/dataHealth';
import {
  ConfidenceBadge,
  FilterChip,
  StatusBadge,
  formatDateTime,
} from '../shared/inspectorTableHelpers';
import BulkActionBar, { Eye, Pencil, Download, Flag, RefreshCw } from './BulkActionBar';
import DataHealthRowDrawer from './DataHealthRowDrawer';
import DataHealthList, { type DataHealthColumn } from './DataHealthList';

interface Props {
  clientId: string;
  isSuperadmin: boolean;
  onToast: (msg: string) => void;
}

const REASON_FILTERS: { key: HiddenReasonShort | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'out_of_scope', label: 'Fuera de scope' },
  { key: 'low_confidence', label: 'Baja confianza' },
  { key: 'manual', label: 'Manual' },
  { key: 'duplicate', label: 'Duplicado' },
];

const HiddenItemsTab: React.FC<Props> = ({ clientId, isSuperadmin, onToast }) => {
  const [items, setItems] = useState<DataHealthItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [backendDown, setBackendDown] = useState(false);

  const [reason, setReason] = useState<HiddenReasonShort | 'all'>('all');
  const [search, setSearch] = useState('');

  // Orden del servidor (DataHealthList → onSort). El backend refetchea con estos params.
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerItem, setDrawerItem] = useState<DataHealthItem | null>(null);

  // Guard anti-race: cada request captura un token; solo el más nuevo escribe
  // estado. Evita que una respuesta vieja (ej. del cliente anterior, que llega
  // tarde) pise la del cliente actual al cambiar de cliente. (T-B)
  const reqIdRef = useRef(0);

  // Carga.
  const loadData = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    setBackendDown(false);
    try {
      const r = await listHidden(clientId, {
        page,
        limit: pageSize,
        reason: reason !== 'all' ? reason : undefined,
        search: search.trim() || undefined,
        sortBy: sortBy || undefined,
        sortDir: sortDir || undefined,
      });
      if (myReq !== reqIdRef.current) return; // respuesta stale
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      if (isBackendUnavailable(e)) {
        setBackendDown(true);
      } else {
        setErr(errorMessage(e));
      }
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [clientId, page, pageSize, reason, search, sortBy, sortDir]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Reset de selección y page al cambiar filtros.
  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [reason, search]);

  // Cambio de orden → volver a página 1 (refetch lo dispara el cambio de sortBy/sortDir).
  const handleSort = useCallback((by: string, dir: 'asc' | 'desc') => {
    setSortBy(by);
    setSortDir(dir);
    setPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pageAllSelected = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id_catalogo)),
    [items, selected]
  );

  const togglePage = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        items.forEach((it) => next.add(it.id_catalogo));
      } else {
        items.forEach((it) => next.delete(it.id_catalogo));
      }
      return next;
    });
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleBulkUnhide = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`¿Mostrar ${selected.size} producto(s)? Volverán a aparecer en publicaciones y stats.`)) {
      return;
    }
    try {
      const ids = Array.from(selected);
      await bulkUnhide(clientId, ids);
      onToast(`Mostrados ${ids.length} producto(s)`);
      setSelected(new Set());
      void loadData();
    } catch (e) {
      if (isBackendUnavailable(e)) {
        onToast('Backend pendiente de deploy');
      } else {
        onToast(`Error: ${errorMessage(e)}`);
      }
    }
  };

  // Backend down banner.
  if (backendDown && !loading) {
    return (
      <div className="space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
          <div className="font-medium text-amber-800 mb-1">Backend pendiente de deploy</div>
          <div className="text-amber-700 text-xs">
            Los endpoints de visibilidad (`/catalog?visibility=hidden`) todavía no están
            disponibles. La UI está lista — esperando deploy del backend (sub-agente paralelo).
          </div>
          {!isSuperadmin && (
            <div className="text-amber-600 text-[11px] mt-1">
              Tu rol no es superadmin: si querés probar con datos mock pedí a un superadmin que
              active el toggle.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Header info: superadmin puede toggle "Incluir hidden" — pero en esta tab
  // siempre se muestran SOLO hidden, así que el toggle es informativo nomás.

  // Config de columnas para DataHealthList. Anchos FIJOS (table-layout:fixed):
  // title es la columna flexible (sin width). Estilo tomado de OCULTOS.
  const columns: DataHealthColumn<DataHealthItem>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: false,
      render: (it) => (
        <span className="block truncate" title={it.title || ''}>
          {it.title || <span className="text-gray-400">—</span>}
        </span>
      ),
    },
    {
      key: 'marca',
      header: 'Marca',
      width: '9rem',
      render: (it) =>
        it.catalog_brand || it.marca || <span className="text-red-400 italic">null</span>,
    },
    {
      key: 'subcat',
      header: 'Subcat',
      width: '9rem',
      render: (it) => it.subcategory || <span className="text-red-400 italic">null</span>,
    },
    {
      key: 'conf',
      header: 'Conf',
      width: '5rem',
      align: 'right',
      render: (it) => <ConfidenceBadge value={it.confidence_score} />,
    },
    {
      key: 'motivo',
      header: 'Motivo',
      width: '9rem',
      render: (it) =>
        it.hidden_reason ? (
          <StatusBadge
            kind={
              it.hidden_reason === 'out_of_scope' || it.hidden_reason === 'hidden_out_of_scope'
                ? 'hidden_out_of_scope'
                : it.hidden_reason === 'low_confidence' || it.hidden_reason === 'hidden_low_confidence'
                  ? 'hidden_low_confidence'
                  : it.hidden_reason === 'duplicate' || it.hidden_reason === 'hidden_duplicate'
                    ? 'hidden_duplicate'
                    : 'hidden_manual'
            }
          />
        ) : (
          '—'
        ),
    },
    {
      key: 'hidden_at',
      header: 'Hidden at',
      width: '9rem',
      cellClassName: 'tabular-nums',
      render: (it) => formatDateTime(it.hidden_at),
    },
    {
      key: 'sitio',
      header: 'Sitio',
      width: '8rem',
      sortable: false,
      render: (it) =>
        it.url ? (
          <a
            href={it.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800"
          >
            {it.sitio || '—'}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        ) : (
          it.sitio || '—'
        ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex flex-wrap gap-1.5">
          {REASON_FILTERS.map((f) => (
            <FilterChip
              key={f.key}
              active={reason === f.key}
              label={f.label}
              onClick={() => setReason(f.key)}
            />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título…"
            className="text-[11px] border border-gray-300 rounded px-2 py-1 w-56 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="p-0.5 text-gray-400 hover:text-gray-600"
              title="Limpiar"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        {isSuperadmin && (
          <span className="text-[10px] text-gray-500 italic">
            (superadmin: visualizando productos ocultos — incluir/no incluir es global desde Publicaciones)
          </span>
        )}
      </div>

      {/* Error banner (el vacío/carga los maneja DataHealthList) */}
      {err && (
        <div className="mb-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {err}
        </div>
      )}

      {/* Listado unificado (DataHealthList) con selección múltiple + drawer overlay */}
      <DataHealthList<DataHealthItem>
        columns={columns}
        rows={items}
        getRowId={(it) => it.id_catalogo}
        onRowClick={(it) => setDrawerItem(it)}
        activeRowId={drawerItem?.id_catalogo ?? null}
        listId="datahealth-ocultos"
        onSort={handleSort}
        sortBy={sortBy}
        sortDir={sortDir}
        loading={loading}
        emptyState={
          <div className="py-2 text-gray-500">
            <EyeOff className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            No hay productos ocultos para este cliente.
            <div className="text-[11px] mt-1 text-gray-400">
              Cuando ocultes un producto desde la grilla o vía LLM (out of scope), aparecerá acá.
            </div>
          </div>
        }
        selection={{
          selected,
          pageAllSelected,
          onTogglePage: togglePage,
          onToggleOne: toggleOne,
        }}
        pagination={{
          idPrefix: 'ocultos',
          pageIndex: page - 1,
          pageSize,
          totalRows: total,
          totalPages,
          onPageChange: (newIndex) => setPage(newIndex + 1),
          onPageSizeChange: (newSize) => {
            setPageSize(newSize);
            setPage(1);
          },
        }}
        overlay={
          drawerItem ? (
            <DataHealthRowDrawer
              clientId={clientId}
              item={drawerItem}
              onClose={() => setDrawerItem(null)}
              onPrev={() => {
                const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
                if (i > 0) setDrawerItem(items[i - 1]);
              }}
              onNext={() => {
                const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
                if (i >= 0 && i < items.length - 1) setDrawerItem(items[i + 1]);
              }}
              hasPrev={items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo) > 0}
              hasNext={(() => {
                const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
                return i >= 0 && i < items.length - 1;
              })()}
            />
          ) : null
        }
      />

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selected.size}
        onClear={() => setSelected(new Set())}
        actions={[
          {
            id: 'unhide',
            label: 'Mostrar',
            icon: Eye,
            variant: 'success',
            onClick: handleBulkUnhide,
          },
          {
            id: 'edit-brand',
            label: 'Editar marca',
            icon: Pencil,
            variant: 'primary',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
          {
            id: 'recatalog',
            label: 'Re-catalogar',
            icon: RefreshCw,
            variant: 'warning',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
          {
            id: 'export',
            label: 'Exportar CSV',
            icon: Download,
            variant: 'neutral',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
          {
            id: 'report',
            label: 'Reportar TomaDatos',
            icon: Flag,
            variant: 'neutral',
            onClick: () => {},
            disabled: true,
            disabledHint: 'Próximamente (v2)',
          },
        ]}
      />
    </div>
  );
};

export default HiddenItemsTab;
